import { format } from 'date-fns';
import type { MetadataNoCounts, ResourceListOptions } from '#dn-types/api.ts';
import type { AuditLog, TargetType } from '#dn-types/audit-log.ts';
import { getBlob, getList } from './fetch.ts';

export const DEFAULT_AUDIT_LOG_PAGE_SIZE = 25;

// GET /audit-logs
export type GetAuditLogsResult = AuditLog[];
type GetAuditLogsVariables = ResourceListOptions & { targetType?: TargetType; targetId?: string };
export function getAuditLogs({
  cursor,
  pageSize = DEFAULT_AUDIT_LOG_PAGE_SIZE,
  targetType,
  targetId,
  init,
}: GetAuditLogsVariables = {}) {
  const params = new URLSearchParams({ includeCounts: 'false', pageSize: String(pageSize), sortDirection: 'desc' });
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (targetType) {
    params.set('filter.targetType', targetType);
  }
  if (targetId) {
    params.set('filter.targetID', targetId);
  }

  return getList<GetAuditLogsResult, MetadataNoCounts>(`/v1/audit-logs?${params.toString()}`, init).then((res) => {
    // If the user paginates backwards, we may send a cursor which results in loading the "first" page of results
    // but with fewer results than the standard page size. Refetch the first page without a cursor to get a full result set.
    // We normally rely on page count here, but we don't have that for audit logs (too slow), so we count the actual results
    if (cursor && !res.metadata.hasPrevPage && res.data.length < pageSize) {
      return getList<GetAuditLogsResult, MetadataNoCounts>(
        `/v1/audit-logs?includeCounts=true&pageSize=${pageSize}${targetType ? `&filter.targetType=${encodeURIComponent(targetType)}` : ''}`
      );
    }

    return res;
  });
}

// GET /audit-logs.csv
export function getAuditLogsBlobURL(
  {
    targetType,
    targetId,
    since,
  }: {
    targetType?: string;
    targetId?: string;
    since?: Date;
  },
  init?: RequestInit
): Promise<string> {
  const params = new URLSearchParams();
  if (targetType) {
    params.set('filter.targetType', targetType);
  }
  if (targetId) {
    params.set('filter.targetID', targetId);
  }
  if (since) {
    params.set('filter.sinceDate', format(since, 'yyyy-MM-dd'));
  }
  return getBlob(`/v1/audit-logs.csv?${params.toString()}`, { headers: { Accept: 'text/csv' }, ...init }).then(
    (res) => {
      return URL.createObjectURL(res);
    }
  );
}
